import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

type InputType =
  | 'color'
  | 'email'
  | 'hidden'
  | 'password'
  | 'range'
  | 'search'
  | 'number'
  | 'tel'
  | 'text'
  | 'file'
  | 'url'
type InputToggle = 'on' | 'off'
type InputDensity = 'large' | 'medium' | 'small'
type InputState = 'info' | 'warning' | 'danger' | 'success'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/input?tab=designer).
 *
 * @slot action - Botão à direita do input.
 * @slot icon - Ícone à esquerda do input.
 * @slot help-text - Personalização do texto de ajuda.
 * @slot feedback - Mensagem de feedback como resposta específica a uma interação do usuário com o input. Pode ser feedback de erro, aviso, sucesso ou informação.
 */
@Component({
  tag: 'br-input',
  styleUrl: 'input.scss',
  shadow: true,
  formAssociated: true,
})
export class Input {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrInputElement

  @AttachInternals() elementInternals?: ElementInternals

  /**
   * Especifica o tipo de entrada do campo.
   */
  @Prop({ reflect: true }) readonly type: InputType = 'text'

  /**
   * Controla o comportamento de preenchimento automático do navegador para o input.
   */
  @Prop({ reflect: true }) readonly autocomplete?: InputToggle

  /**
   * Ajusta a densidade, alterando o espaçamento interno para um visual mais compacto ou mais expandido.
   */
  @Prop({ reflect: true }) readonly density: InputDensity = 'medium'

  /**
   * Desativa o input, tornando-o não interativo.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Se verdadeiro, o rótulo e o input estarão na mesma linha (layout inline).
   */
  @Prop({ reflect: true }) readonly isInline: boolean = false

  /**
   * Se verdadeiro, o input terá destaque visual.
   */
  @Prop({ reflect: true }) readonly isHighlight: boolean = false

  /**
   * Define o estado do input
   * @remarks
   * O estado é propagado para o slot de feedback, mas também pode ser controlado diretamente pelo conteúdo do slot.
   */
  @Prop({ reflect: true }) readonly state?: InputState

  /**
   * Texto exibido como rótulo do input.
   */
  @Prop({ reflect: true }) readonly label?: string

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-input-${inputId++}`

  /**
   * Nome do input, utilizado para identificação em formulários.
   */
  @Prop({ reflect: true }) readonly name?: string

  /**
   * Texto exibido dentro do input quando está vazio, fornecendo uma dica ou sugestão ao usuário.
   */
  @Prop({ reflect: true }) readonly placeholder?: string

  /**
   * Se verdadeiro, o valor do input é exibido, mas não pode ser editado pelo usuário.
   */
  @Prop({ reflect: true }) readonly readonly: boolean = false

  /**
   * Se verdadeiro, o input é obrigatório e deve ser preenchido antes que o formulário possa ser enviado.
   */
  @Prop({ reflect: true }) readonly required: boolean = false

  /**
   * Valor exibido no input.
   * Pode ser alterado pelo usuário se a propriedade `readonly` não estiver ativa.
   */
  @Prop({ reflect: true, mutable: true }) value?: string

  /**
   * Largura do campo de entrada (por exemplo, '88px').
   * Quando definido, sobrescreve a largura padrão de 100%.
   */
  @Prop({ reflect: true }) readonly controlWidth?: string

  /**
   * Remove a borda do input quando não está em foco.
   * Útil para composições contextuais (ex.: paginação).
   */
  @Prop({ reflect: true }) readonly borderless: boolean = false

  /**
   * Texto adicional que fornece ajuda ou informações sobre o input.
   */
  @Prop({ reflect: true }) readonly helpText?: string

  /**
   * Controla a correção automática do texto.
   */
  @Prop({ reflect: true }) readonly autocorrect: InputToggle = 'off'

  /**
   * Define o valor mínimo para campos de entrada numéricos.
   */
  @Prop({ reflect: true }) readonly min?: number

  /**
   * Define o valor máximo para campos de entrada numéricos.
   */
  @Prop({ reflect: true }) readonly max?: number

  /**
   * Define o comprimento mínimo do valor do campo de entrada.
   */
  @Prop({ reflect: true }) readonly minlength?: number

  /**
   * Define o comprimento máximo do valor do campo de entrada.
   */
  @Prop({ reflect: true }) readonly maxlength?: number

  /**
   * Se verdadeiro, permite a seleção de múltiplos arquivos.
   */
  @Prop({ reflect: true }) readonly multiple: boolean = false

  /**
   * Define o padrão de entrada para validação.
   */
  @Prop({ reflect: true }) readonly pattern?: string

  /**
   * Máscara aplicada ao valor digitado (use `#` para marcar posições numéricas).
   */
  @Prop({ reflect: true }) readonly mask?: string

  /**
   * Define o valor do passo para campos de entrada numéricos.
   */
  @Prop({ reflect: true }) readonly step?: number

  /**
   * Texto exibido no botão de ação à direita do input.
   */
  @Prop({ reflect: true }) readonly actionLabel?: string

  /**
   * Define o tabindex do botão de ação.
   * Útil para remover o botão da sequência de tabulação quando o foco é gerenciado externamente (ex.: br-select).
   */
  @Prop({ reflect: true }) readonly actionTabIndex?: number

  @State() isPasswordVisible: boolean = false

  @State() internalState?: InputState

  @State() hasIconSlot: boolean = false

  @State() hasActionSlot: boolean = false

  @State() hasFeedbackSlot: boolean = false

  private normalizeState(value: unknown): InputState | undefined {
    return value === 'info' || value === 'warning' || value === 'danger' || value === 'success' ? value : undefined
  }

  componentWillLoad() {
    this.hasIconSlot = this.hasSlotContent('icon')
    this.hasActionSlot = this.hasSlotContent('action')
    this.hasFeedbackSlot = this.hasSlotContent('feedback')
    this.internalState = this.normalizeState(this.state)
    this.applyMaskToValue()

    if (this.hasFeedbackSlot) {
      this.initializeStateWithFeedback()
    }
  }

  componentDidLoad() {
    if (this.hasFeedbackSlot) {
      const slot = this.el.shadowRoot?.querySelector('slot[name="feedback"]')

      if (slot !== null && slot !== undefined) {
        slot.addEventListener('slotchange', () => {
          this.propagateStateToFeedback()
        })
      }

      // 🔥 novo: executa já no load
      this.propagateStateToFeedback()
    }
  }

  /**
   * Inicializa o estado interno do input com base no estado do elemento de mensagem de feedback, se presente.
   */
  private initializeStateWithFeedback() {
    const initialMessageElement = this.el.querySelector('br-message')

    if (initialMessageElement !== null && initialMessageElement !== undefined) {
      const messageState = initialMessageElement.getAttribute('state')
      this.internalState = this.normalizeState(messageState ?? this.state)
    } else {
      this.internalState = this.normalizeState(this.state)
    }
  }

  /**
   * Propaga o estado interno do input para os elementos de mensagem de feedback.
   * @returns void
   */
  private propagateStateToFeedback() {
    const messageElements = this.el.querySelectorAll('br-message')

    if (messageElements.length === 0) {
      this.internalState = this.normalizeState(this.state)
      return
    }

    this.internalState = this.normalizeState(this.state)

    messageElements.forEach((message) => {
      const messageState = this.normalizeState(message.getAttribute('state'))

      if (this.internalState === undefined) {
        message.setAttribute('hidden', 'true')
        return
      }

      if (messageState === this.internalState) {
        message.removeAttribute('hidden')
      } else {
        message.setAttribute('hidden', 'true')
      }
    })
  }

  /**
   * Retorna um mapa de classes CSS para o input, baseado no estado interno e nas propriedades do componente.
   * @returns Um objeto representando as classes CSS a serem aplicadas ao input.
   */
  private getInputCssClassMap(): CssClassMap {
    return {
      'br-input': true,
      'input-button': this.hasActionSlot,
      small: this.density === 'small',
      large: this.density === 'large',
      medium: this.density === 'medium',
      'input-inline': this.isInline,
      'input-highlight': this.isHighlight,
      success: this.internalState === 'success',
      danger: this.internalState === 'danger',
      info: this.internalState === 'info',
      warning: this.internalState === 'warning',
    }
  }

  /**
   * Valor atualizado do input
   */
  @Event() valueChange!: EventEmitter<string>

  @Watch('value')
  protected handleValueChange(newValue: string) {
    if (!this.hasMaskConfigured()) {
      return
    }

    const maskedValue = this.applyMask(newValue ?? '')
    if (maskedValue !== newValue) {
      this.value = maskedValue
    }
  }

  @Watch('mask')
  protected handleMaskChange() {
    this.applyMaskToValue()
  }

  @Watch('state')
  protected handleStateChange(newState: InputState | undefined) {
    this.internalState = this.normalizeState(newState)

    // 🔥 novo comportamento centralizado
    this.propagateStateToFeedback()
  }

  /**
   * Manipula o evento de input, aplicando máscara se necessário e emitindo o valor atualizado.
   * @param event Evento de input do elemento HTMLInputElement
   */
  private readonly handleInput = (event: Event) => {
    const inputElement = event.target as HTMLInputElement
    let nextValue = inputElement.value ?? ''

    if (this.hasMaskConfigured()) {
      const maskedValue = this.applyMask(nextValue)
      if (maskedValue !== nextValue) {
        inputElement.value = maskedValue
      }
      nextValue = maskedValue
    }

    this.value = nextValue
    this.valueChange.emit(this.value)
  }

  /**
   * Renderiza o rótulo do input, considerando se está em modo inline ou não.
   * @returns JSX.Element representando o rótulo do input.
   */
  private readonly renderLabel = () => {
    if (typeof this.label === 'string' && this.label.length > 0) {
      if (this.isInline) {
        return (
          <div class="input-label">
            <label part="label" class="text-nowrap" htmlFor={this.customId}>
              {this.label}
            </label>
          </div>
        )
      } else {
        return (
          <label part="label" htmlFor={this.customId}>
            {this.label}
          </label>
        )
      }
    }
  }

  /**
   * Verifica se um slot específico possui conteúdo.
   * @param slotName Nome do slot a ser verificado
   * @returns true se o slot possui conteúdo, false caso contrário
   */
  private hasSlotContent(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null
  }

  /**
   * Renderiza o input do componente, aplicando as propriedades e eventos necessários.
   * @returns JSX.Element representando o input.
   */
  private readonly renderInput = () => {
    return (
      <input
        class={this.isHighlight ? 'input-highlight' : undefined}
        id={this.customId}
        style={this.controlWidth !== undefined && this.controlWidth !== null ? { width: this.controlWidth } : undefined}
        autocomplete={this.autocomplete}
        disabled={this.disabled}
        autoCorrect={this.autocorrect}
        min={this.min}
        max={this.max}
        minLength={this.minlength}
        maxLength={this.maxlength}
        multiple={this.multiple}
        name={this.name}
        pattern={this.pattern}
        step={this.step}
        onInput={this.handleInput}
        placeholder={this.placeholder}
        readonly={this.readonly}
        required={this.required}
        title={this.el.title}
        type={this.type}
        value={this.value}
      />
    )
  }

  /**
   * Renderiza o texto de ajuda do input, considerando se está definido como string ou slot.
   * @returns JSX.Element representando o texto de ajuda.
   */
  private readonly renderHelpText = () => {
    return typeof this.helpText === 'string' && this.helpText.length > 0 ? (
      <p>{this.helpText}</p>
    ) : (
      <slot name="help-text"></slot>
    )
  }

  /**
   * Renderiza o ícone de visibilidade da senha, alternando entre mostrar e esconder.
   * @returns JSX.Element representando o ícone de visibilidade da senha.
   */
  private readonly renderIconPassword = () => {
    const iconName = this.isPasswordVisible ? 'fa-solid:eye-slash' : 'fa-solid:eye'
    return <br-icon icon-name={iconName}></br-icon>
  }

  /**
   * Alterna a visibilidade da senha, mudando o tipo do input entre 'text' e 'password'.
   * @returns void
   */
  private readonly togglePasswordVisibility = () => {
    const input = this.el.shadowRoot?.querySelector('input')
    if (input === null || input === undefined) {
      return
    }

    this.isPasswordVisible = !this.isPasswordVisible
    input.type = this.isPasswordVisible ? 'text' : 'password'
  }

  /**
   * Renderiza o slot de ícone, caso exista.
   * @returns JSX.Element representando o slot de ícone.
   */
  private renderIconSlot() {
    if (this.hasSlotContent('icon')) {
      return (
        <div class="input-icon">
          <slot name="icon"></slot>
        </div>
      )
    }
  }

  /**
   * Renderiza o botão de ação do input, considerando o tipo de input e slots disponíveis.
   * @returns JSX.Element representando o botão de ação.
   */
  private renderButton() {
    if (this.type === 'password') {
      return (
        <br-button
          class="button-transform"
          shape="circle"
          density="small"
          aria-label={this.isPasswordVisible ? 'Esconder senha' : 'Mostrar senha'}
          aria-pressed={this.isPasswordVisible ? 'true' : 'false'}
          onClick={this.togglePasswordVisibility}
        >
          {this.renderIconPassword()}
        </br-button>
      )
    } else if (this.hasSlotContent('action')) {
      return (
        <br-button
          class="button-transform"
          shape="circle"
          density="small"
          aria-label={this.actionLabel}
          customTabIndex={this.actionTabIndex}
        >
          <slot name="action"></slot>
        </br-button>
      )
    }
    return null
  }

  /**
   * Renderiza o conteúdo do input quando está em linha, considerando se há ícone ou não.
   * @returns JSX.Element representando o conteúdo do input em linha.
   */
  private readonly renderInlineInputContent = () => {
    if (this.hasIconSlot) {
      return <div class="input-content">{this.renderInputWithIcon()}</div>
    } else {
      return <div class="input-content">{this.renderInputWithoutIcon()}</div>
    }
  }

  /**
   * Renderiza o conteúdo do input, considerando se há ícone ou não.
   * @returns JSX.Element representando o conteúdo do input.
   */
  private readonly renderInputContent = () => {
    return this.hasIconSlot ? this.renderInputWithIcon() : this.renderInputWithoutIcon()
  }

  /**
   * Renderiza o conteúdo do input com ícone, organizando os elementos em um grupo e aplicando estilos de largura quando necessário.
   */
  private readonly renderInputWithIcon = () => {
    return (
      <div
        class="input-group"
        style={this.controlWidth !== undefined && this.controlWidth !== null ? { width: this.controlWidth } : undefined}
      >
        {this.renderIconSlot()}
        {this.renderInput()}
        {this.renderButton()}
        {this.renderHelpText()}
      </div>
    )
  }

  /**
   * Renderiza o conteúdo do input sem ícone, organizando os elementos em um grupo.
   * @returns JSX.Element representando o conteúdo do input sem ícone.
   */
  private readonly renderInputWithoutIcon = () => {
    return [this.renderInput(), this.renderButton(), this.renderHelpText()]
  }

  /**
   * Renderiza o slot de feedback, caso exista, permitindo que mensagens de feedback sejam exibidas em resposta a interações do usuário com o input.
   * @returns JSX.Element representando o slot de feedback.
   */
  private renderFeedback() {
    if (this.internalState === undefined) {
      return null
    }

    return <slot name="feedback"></slot>
  }

  /**
   * Aplica a máscara configurada ao valor do input, se houver.
   * @returns void
   */
  private applyMaskToValue() {
    if (!this.hasMaskConfigured() || this.value === undefined || this.value === null) {
      return
    }

    const maskedValue = this.applyMask(this.value)
    if (this.value !== maskedValue) {
      this.value = maskedValue
    }
  }

  /**
   * Aplica a máscara configurada a um valor específico.
   * @param value O valor a ser mascarado.
   * @returns O valor mascarado.
   */
  private applyMask(value: string): string {
    if (!this.hasMaskConfigured()) {
      return value ?? ''
    }

    const mask = this.mask || ''

    const digits = (value ?? '').replace(/\D/g, '')
    if (digits.length === 0) {
      return ''
    }

    let maskedValue = ''
    let digitIndex = 0

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      const maskChar = mask[i]

      if (maskChar === '#') {
        maskedValue += digits[digitIndex]
        digitIndex++
      } else {
        maskedValue += maskChar
      }
    }

    return maskedValue
  }

  /**
   * Verifica se uma máscara está configurada.
   * @returns boolean indicando se uma máscara está configurada.
   */
  private hasMaskConfigured(): boolean {
    return typeof this.mask === 'string' && this.mask.length > 0
  }

  /**
   * Renderiza o componente de input.
   * @returns JSX.Element representando o componente de input.
   */
  render() {
    return (
      <Host>
        <div class={this.getInputCssClassMap()}>
          {this.renderLabel()}

          {this.isInline ? this.renderInlineInputContent() : this.renderInputContent()}

          {this.renderFeedback()}
        </div>
      </Host>
    )
  }
}

let inputId = 0
